/**
 * F009: Golden Path Progress Stepper — Golden Path Wave 3
 *
 * Horizontal progress stepper guiding brokers through the Golden Path:
 * Upload → Spread → Analyse → Match → Generate Memo.
 * Auto-detects completion via polling API. Dismissible with localStorage persistence.
 *
 * Spec: specs/golden-path/09-golden-path-stepper.spec.md
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Upload,
  Table2,
  Calculator,
  Search,
  FileText,
  CheckCircle2,
  X,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

// =============================================================================
// Types
// =============================================================================

interface StepStatus {
  step: number;
  label: string;
  complete: boolean;
  completedAt: string | null;
}

interface GoldenPathStatus {
  steps: StepStatus[];
  currentStep: number | null;
  overallComplete: boolean;
}

interface GoldenPathStepperProps {
  dealId: string;
  onNavigateTab?: (tabValue: string) => void;
}

// =============================================================================
// Step config
// =============================================================================

const STEP_ICONS = [Upload, Table2, Calculator, Search, FileText];

const STEP_CTAS: Array<{ text: string; action: string }> = [
  { text: "Upload financial statements", action: "documents" },
  { text: "Spread financial data", action: "financials" },
  { text: "Calculate serviceability", action: "financials" },
  { text: "Match lenders", action: "lender-matches" },
  { text: "Generate credit memo", action: "financials" },
];

// =============================================================================
// Component
// =============================================================================

export function GoldenPathStepper({ dealId, onNavigateTab }: GoldenPathStepperProps) {
  const storageKey = `golden-path-dismissed-${dealId}`;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === "true";
    } catch {
      return false;
    }
  });

  const POLL_INTERVAL_MS = 30_000;

  const { data: status, isLoading } = useQuery<GoldenPathStatus>({
    queryKey: [`/api/financial/deals/${dealId}/golden-path-status`],
    enabled: !!dealId && !dismissed,
    refetchInterval: (query) => (query.state.data?.overallComplete ? false : POLL_INTERVAL_MS),
  });

  function handleDismiss() {
    setDismissed(true);
    try {
      localStorage.setItem(storageKey, "true");
    } catch { /* ignore */ }
  }

  function handleReopen() {
    setDismissed(false);
    try {
      localStorage.removeItem(storageKey);
    } catch { /* ignore */ }
  }

  function handleCtaClick(stepIndex: number) {
    const cta = STEP_CTAS[stepIndex];
    if (cta && onNavigateTab) {
      onNavigateTab(cta.action);
    }
  }

  // Show "Show progress" link when dismissed
  if (dismissed) {
    return (
      <div data-testid="golden-path-stepper-collapsed">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleReopen}
          className="text-xs text-muted-foreground p-0 h-auto underline"
          data-testid="button-show-progress"
        >
          Show progress
        </Button>
      </div>
    );
  }

  // Loading skeleton
  if (isLoading || !status) {
    return (
      <Card data-testid="golden-path-stepper" className="mb-2">
        <CardContent className="py-3 px-4">
          <div className="flex items-center justify-center py-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const { steps, currentStep, overallComplete } = status;

  return (
    <Card data-testid="golden-path-stepper" className="mb-2 relative">
      {/* Dismiss button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleDismiss}
        className="absolute top-1 right-1 h-6 w-6 p-0"
        data-testid="button-dismiss-stepper"
      >
        <X className="h-3.5 w-3.5" />
      </Button>

      <CardContent className="py-3 px-4">
        {/* Horizontal stepper (desktop) / Vertical (mobile) */}
        <div
          className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-0"
          data-testid="stepper-steps"
        >
          {steps.map((step, index) => {
            const Icon = STEP_ICONS[index];
            const isActive = currentStep !== null && step.step === currentStep;
            const isPending = currentStep !== null && step.step > currentStep;
            const cta = STEP_CTAS[index];

            return (
              <div
                key={step.step}
                className="flex items-center gap-0 flex-1"
                data-testid={`step-${step.step}`}
              >
                {/* Step indicator */}
                <div className="flex flex-col items-center gap-1 min-w-0">
                  <div
                    className={cn(
                      "flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors",
                      step.complete && "bg-green-50 border-green-500",
                      isActive && !step.complete && "border-primary bg-primary/5",
                      isPending && "border-muted-foreground/30 bg-muted/20",
                    )}
                  >
                    {step.complete ? (
                      <CheckCircle2
                        className="h-5 w-5 text-green-600"
                        data-testid={`step-${step.step}-check`}
                      />
                    ) : (
                      <Icon
                        className={cn(
                          "h-4 w-4",
                          isActive ? "text-primary" : "text-muted-foreground/50",
                        )}
                      />
                    )}
                  </div>

                  <span
                    className={cn(
                      "text-[10px] leading-tight text-center max-w-[80px]",
                      step.complete && "text-green-700 font-medium",
                      isActive && !step.complete && "text-primary font-medium",
                      isPending && "text-muted-foreground/50",
                    )}
                  >
                    {step.label}
                  </span>

                  {/* Active step CTA */}
                  {isActive && !step.complete && cta && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[10px] text-primary underline p-0 h-auto leading-tight"
                      onClick={() => handleCtaClick(index)}
                      data-testid={`step-${step.step}-cta`}
                    >
                      {cta.text}
                    </Button>
                  )}
                </div>

                {/* Connector line */}
                {index < steps.length - 1 && (
                  <div
                    className={cn(
                      "hidden sm:block flex-1 h-0.5 mx-2 rounded",
                      step.complete ? "bg-green-400" : "bg-muted-foreground/20",
                    )}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Overall complete message */}
        {overallComplete && (
          <div
            className="text-center text-xs text-green-700 font-medium mt-2"
            data-testid="stepper-complete"
          >
            Golden Path complete
          </div>
        )}
      </CardContent>
    </Card>
  );
}
